import React, { useEffect, useMemo, useState } from "react";
import ViewFileModal from "../components/ViewFileModal";
import mimeTypeObj from "../helper/mimeTypeObj";
import { useNavigate, useOutletContext, useParams, useLocation } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Upload,
} from "antd";
import { MinusCircleOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { BrowserView, MobileView, isMobile } from "react-device-detect";
import { useDispatch } from "react-redux";
import PageLayout from "../components/PageLayout";
import PageTitle from "../components/PageTitle";
import InputPrefix from "../helper/InputPrefix";
import PageBtns from "../components/PageBtns";
import MobileBtns from "../components/MobileBtns";
import {
  FinDocCompanyOptions,
  FinDocDocTypeObj,
  FinDocDocTypeOptions,
} from "../helper/FinDocDocTypeObj";
import FinDocStatusObj from "../helper/FinDocStatusObj";
import {
  addFinDoc,
  agreeFinDoc,
  cancelFinDoc,
  financeUploadFinDoc,
  getFinDocDetail,
  mgrAgreeFinDoc,
  mgrRejectFinDoc,
  queryFinDocLog,
  rejectFinDoc,
} from "../newApi/findoc.api";
import { getFinDocSitActor, getFinDocSitConfig } from "../newApi/findoc.mock";
import { acquireAccessToken } from "../msalInstance";
import { getAllWiadvanceUsers, getManagerChainList, getMSId, getMyManager } from "../redux/action/graph";
import { setViewFileModalVisible, viewFiledata } from "../redux/action/Components";

const { TextArea } = Input;

const financeGroup = [
  { name: "Kaia Chen", email: "kaia.chen@wiadvance.com" },
  { name: "Sara Yu", email: "sara.yu@wiadvance.com" },
  { name: "Stephanie Chen", email: "stephanie.chen@wiadvance.com" },
];

const michaelApprover = { name: "總經理", email: "michael.hsia@wiadvance.com" };
const stephanieApprover = { name: "Stephanie Chen", email: "stephanie.chen@wiadvance.com" };

const docRequirementMap = {
  noBounceCert: {
    periodLabel: "期間",
    periodRequired: true,
    periodEndRequired: true,
    originalRequired: true,
  },
  tax401Cert: {
    periodLabel: "期間",
    periodRequired: true,
    periodEndRequired: true,
  },
  financialReport: {
    periodLabel: "年月份",
    periodRequired: true,
  },
  taxReport: {
    periodLabel: "年月份",
    periodRequired: true,
  },
  companyRegForm: {},
  otherFinDoc: {
    remarkRequired: true,
  },
};

const isMichaelApprover = (value) => {
  const normalized = String(value || "").toLowerCase();
  return normalized === michaelApprover.email.toLowerCase() || normalized.includes("michael hsia") || normalized.includes("夏志豪");
};

const normalizeAuditorDisplayName = (name, mail) => {
  if (isMichaelApprover(mail) || isMichaelApprover(name)) {
    return michaelApprover.name;
  }

  return name;
};

const normalizeAuditor = (auditor) => {
  if (!auditor) {
    return auditor;
  }

  const displayName = normalizeAuditorDisplayName(auditor.displayName || auditor.name, auditor.mail || auditor.email);

  return {
    ...auditor,
    displayName,
    name: normalizeAuditorDisplayName(auditor.name || auditor.displayName, auditor.mail || auditor.email),
  };
};

const getDisplayAuditorName = (record) => {
  if (!record) {
    return "-";
  }

  if (record.findoc_status === "pendingMgr" || isMichaelApprover(record.next_auditor_mail) || isMichaelApprover(record.next_auditor_name)) {
    return michaelApprover.name;
  }

  return normalizeAuditorDisplayName(record.next_auditor_name, record.next_auditor_mail) || "-";
};

const isCurrentMichaelAuditor = (detail, actor) => {
  const nextAuditorMails = String(detail?.next_auditor_mail || "")
    .toLowerCase()
    .split(",")
    .map((item) => item.trim());

  return (
    isMichaelApprover(actor?.email) ||
    isMichaelApprover(actor?.name) ||
    ((isMichaelApprover(detail?.next_auditor_mail) || isMichaelApprover(detail?.next_auditor_name)) &&
      nextAuditorMails.includes(String(actor?.email || "").toLowerCase()))
  );
};

const getFinanceAuditorPayload = () => ({
  next_auditor_name: financeGroup.map((item) => item.name).join(","),
  next_auditor_mail: financeGroup.map((item) => item.email).join(","),
});

const getSitFallbackManager = (email) => {
  const lowerEmail = String(email || "").toLowerCase();
  // Stephanie 的上層是總經理
  if (lowerEmail === "stephanie.chen@wiadvance.com") {
    return { displayName: michaelApprover.name, mail: michaelApprover.email };
  }
  // 其他人(一般同仁、財務 Kaia/Sara…)模擬送單 → 上層主管 Stephanie
  return { displayName: stephanieApprover.name, mail: stephanieApprover.email };
};

const getSitFallbackManagerChain = (email) => {
  const lowerEmail = String(email || "").toLowerCase();
  if (lowerEmail === "stephanie.chen@wiadvance.com") {
    return [{ name: "申請人", type: "申請人" }];
  }

  return [
    { name: "申請人", type: "申請人" },
    { name: stephanieApprover.name, type: "上層主管", mail: stephanieApprover.email },
  ];
};

const formatMonthValue = (value) => {
  if (!value) {
    return "";
  }

  return moment.isMoment(value) ? value.format("YYYY-MM") : value;
};

const parseMonthValue = (value) => (value ? moment(value, "YYYY-MM") : null);

const uploadFileProps = {
  beforeUpload: (file) => {
    switch (file.type) {
      case "image/png":
      case "image/gif":
      case "image/jpg":
      case "image/jpeg":
      case "application/pdf":
        return null;
      default:
        notification.error({ message: "錯誤", description: "檔案格式錯誤" });
        return Upload.LIST_IGNORE;
    }
  },
};

export default function FinDocDetail() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useOutletContext();
  const { id } = useParams();
  const sitConfig = getFinDocSitConfig();
  const actor = useMemo(() => {
    if (sitConfig.enabled) {
      return getFinDocSitActor();
    }

    return {
      name: currentUser?.name,
      email: currentUser?.email,
    };
  }, [currentUser?.email, currentUser?.name, sitConfig.enabled]);
  const isAddMode = location.pathname.toLowerCase().endsWith("/add");
  const isAuditMode = location.pathname.toLowerCase().includes("/audit/");
  const isFinanceMode = location.pathname.toLowerCase().includes("/finance/");
  const formLayoutProps = isMobile
    ? {
        layout: "vertical",
        labelAlign: "left",
        labelCol: { span: 24 },
      }
    : {
        layout: "horizontal",
        labelAlign: "left",
        labelCol: { span: 8 },
        wrapperCol: { span: 16 },
      };
  const [pageLoading, setPageLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [logs, setLogs] = useState([]);
  const [uploadedList, setUploadedList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const isUploading = uploadingCount > 0;
  const [signoffProcessList, setSignoffProcessList] = useState([]);
  const [allUserList, setAllUserList] = useState([]);

  const getNextAuditor = async (email) => {
    try {
      const managerResp = await dispatch(getMyManager(email));
      if (managerResp?.mail && !managerResp.error) {
        return normalizeAuditor(managerResp);
      }
    } catch {
      // SIT fallback below keeps local testing usable when Graph cannot resolve mock users.
    }

    if (sitConfig.enabled) {
      return normalizeAuditor(getSitFallbackManager(email));
    }

    return null;
  };

  const buildSignoffProcess = (managerChain = [], currentDetail = null) => {
    const applicant = {
      name: currentDetail?.applicant_name || actor.name || "申請人",
      type: "申請人",
    };
    const managers = managerChain
      .filter((item, index) => index !== 0 && item?.name)
      .map((item) => {
        const name = normalizeAuditorDisplayName(item.name, item.mail || item.email);
        const isGeneralManager = isMichaelApprover(item.mail || item.email) || isMichaelApprover(item.name);

        return {
          ...item,
          name,
          type: isGeneralManager ? michaelApprover.name : item.type === "申請人" ? "上層主管" : item.type,
        };
      });
    const hasGeneralManager = managers.some((item) => item.type === michaelApprover.name || item.name === michaelApprover.name || isMichaelApprover(item.mail || item.email));

    return [
      applicant,
      ...managers,
      ...(hasGeneralManager ? [] : [{ name: michaelApprover.name, type: "總經理", mail: michaelApprover.email }]),
      {
        name: financeGroup.map((item) => item.name).join("、"),
        type: "財務同仁群組",
        mail: financeGroup.map((item) => item.email).join(","),
      },
    ];
  };

  const loadSignoffProcess = async (currentDetail = null) => {
    const applicantMail = currentDetail?.applicant_mail || actor.email;
    if (!applicantMail) {
      setSignoffProcessList(buildSignoffProcess([], currentDetail));
      return;
    }

    try {
      const userResp = await dispatch(getMSId(applicantMail));
      const managerChain = userResp?.id ? await dispatch(getManagerChainList(userResp.id)) : [];
      setSignoffProcessList(buildSignoffProcess(managerChain, currentDetail));
    } catch {
      const fallbackChain = sitConfig.enabled ? getSitFallbackManagerChain(applicantMail) : [];
      setSignoffProcessList(buildSignoffProcess(fallbackChain, currentDetail));
    }
  };

  const refreshDetail = async () => {
    if (!id) {
      return;
    }

    setPageLoading(true);
    try {
      const [detailResp, logResp] = await Promise.all([getFinDocDetail(id), queryFinDocLog(id)]);
      const currentDetail = detailResp?.data?.[0] || null;
      setDetail(currentDetail);
      setLogs(logResp?.data || []);
      if (currentDetail) {
        setUploadedList([
          ...(currentDetail.file_url || []).map((url, index) => ({ uid: `source-${index}`, name: url.split("/").pop(), url })),
          ...(currentDetail.upload_file_url || []).map((url, index) => ({ uid: `result-${index}`, name: url.split("/").pop(), url })),
        ]);
        await loadSignoffProcess(currentDetail);
        form.setFieldsValue({
          ...currentDetail,
          is_agent: currentDetail.is_agent || "N",
          doc_items: (currentDetail.doc_items || []).map((item) => ({
            ...item,
            period_start: parseMonthValue(item.period_start),
            period_end: parseMonthValue(item.period_end),
          })),
          audit_command: "",
          finance_comment: "",
        });
      }
    } catch {
      setDetail(null);
      setLogs([]);
      setUploadedList([]);
      notification.error({ message: "FinDoc 明細載入失敗", description: "無法取得財務文件明細。" });
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("currentMenuItemsKey", isAddMode ? "FinDocApplication" : isAuditMode ? "FinDocPendingAudit" : isFinanceMode ? "FinDocPendingFinance" : "FinDocApplication");
    if (!isAddMode) {
      refreshDetail();
    } else {
      form.setFieldsValue({
        applicant_name: actor.name,
        applicant_mail: actor.email,
        is_agent: "N",
        company: "WIA",
        doc_items: [],
      });
      loadSignoffProcess();
    }
  }, [actor.email, actor.name, form, id, isAddMode, isAuditMode, isFinanceMode]);

  // 呼叫 graph API 取人員資料（被代理人下拉選單，同用印）
  useEffect(() => {
    if (allUserList.length === 0) {
      dispatch(getAllWiadvanceUsers()).then((resp) => {
        if (resp && resp.value) {
          setAllUserList(resp.value.map((item) => ({ label: item.displayName, value: item.mail })));
        }
      });
    }
  }, [allUserList.length, dispatch]);

  const fileUpload = async (option, target = "source") => {
    const { file } = option;
    if (sitConfig.enabled) {
      setUploadedList((prev) => [...prev, { uid: `${target}-${Date.now()}`, name: file.name, url: `mock://${file.name}`, target }]);
      notification.success({ message: "成功", description: "SIT 模式已加入模擬檔案" });
      return;
    }

    setUploadingCount((c) => c + 1);
    try {
      const accessToken = await acquireAccessToken();
      const formData = new FormData();
      formData.append("file", file);

      const resp = await axios({
        method: "post",
        headers: {
          Authorization: accessToken,
          "Content-Type": "multipart/form-data",
        },
        url: `${process.env.REACT_APP_API_URL}Upload/UploadFile`,
        data: formData,
      });

      if (resp?.data?.code === 200) {
        setUploadedList((prev) => [...prev, { uid: `${target}-${Date.now()}`, name: file.name, url: resp.data.data, target }]);
        notification.success({ message: "成功", description: "上傳成功" });
      } else {
        notification.error({ message: "失敗", description: "上傳失敗" });
      }
    } catch (e) {
      notification.error({ message: "失敗", description: "上傳失敗，請稍後再試。" });
    } finally {
      setUploadingCount((c) => c - 1);
    }
  };

  const removeUploadedItem = (file) => {
    setUploadedList((prev) => prev.filter((item) => item.uid !== file.uid));
  };

  // 附件檢視:與用印共用 ViewFileModal(頁內預覽 PDF/圖片、外網提示、下載),不再開新分頁
  const fileNameFromUrl = (url) => {
    const last = String(url || "").split("/").pop();
    try {
      return decodeURI(last).replace(/\+/g, " ");
    } catch (e) {
      return last;
    }
  };

  const toViewFileData = (files) =>
    files.map((item) => {
      const name = item.name || fileNameFromUrl(item.url);
      const ext = name.split(".").pop().toLowerCase();
      return { url: item.url, name, type: mimeTypeObj[ext] || "application/octet-stream" };
    });

  const openViewFileModal = (files, startIndex = 0) => {
    if (!files.length) return;
    const data = toViewFileData(files);
    // 從被點的檔案開始顯示,其餘檔案仍可用「上一個/下一個檔案」切換
    dispatch(viewFiledata(startIndex ? [...data.slice(startIndex), ...data.slice(0, startIndex)] : data));
    dispatch(setViewFileModalVisible(true));
  };

  const renderFileList = (files) => {
    if (!files.length) {
      return <div style={{ color: "#7C7C7C" }}>（無附件）</div>;
    }
    return (
      <>
        <Button onClick={() => openViewFileModal(files)} style={{ marginBottom: 8 }}>
          查看檔案
        </Button>
        {files.map((item, index) => (
          <div key={item.uid || `${item.url}-${index}`}>
            <Button type="link" style={{ padding: 0, height: "auto", whiteSpace: "normal", textAlign: "left" }} onClick={() => openViewFileModal(files, index)}>
              {item.name || fileNameFromUrl(item.url)}
            </Button>
          </div>
        ))}
      </>
    );
  };

  const sourceFiles = uploadedList.filter((item) => item.target !== "result");
  const resultFiles = uploadedList.filter((item) => item.target === "result");
  const canCancel = detail && !["done", "reject", "cancel"].includes(detail.findoc_status) && [detail.applicant_mail, detail.agent_mail].filter(Boolean).map((item) => item.toLowerCase()).includes(String(actor.email).toLowerCase());

  const validateDocItems = (docItems = []) => {
    if (!docItems.length) {
      return "請至少勾選一項申請文件";
    }

    const selectedTypes = new Set();
    for (const item of docItems) {
      if (!item?.doc_type) {
        return "請選擇申請文件";
      }

      if (selectedTypes.has(item.doc_type)) {
        return "申請文件不可重複選擇";
      }
      selectedTypes.add(item.doc_type);

      const requirement = docRequirementMap[item.doc_type] || {};
      if (requirement.periodRequired && !item.period_start) {
        return `${FinDocDocTypeObj[item.doc_type]}需填寫${requirement.periodLabel}`;
      }
      if (requirement.periodEndRequired && !item.period_end) {
        return `${FinDocDocTypeObj[item.doc_type]}需填寫期間迄`;
      }
      if (requirement.originalRequired && !item.need_original) {
        return `${FinDocDocTypeObj[item.doc_type]}需選擇是否需正本`;
      }
      if (requirement.remarkRequired && !item.remark) {
        return `${FinDocDocTypeObj[item.doc_type]}需填寫備註`;
      }
    }

    return null;
  };

  const normalizeDocItems = (docItems = []) =>
    docItems.map((item) => ({
      ...item,
      period_start: formatMonthValue(item.period_start),
      period_end: formatMonthValue(item.period_end),
    }));

  const docColumns = [
    {
      title: "文件類型",
      dataIndex: "doc_type",
      key: "doc_type",
      render: (value) => FinDocDocTypeObj[value] || value,
    },
    {
      title: "期間起",
      dataIndex: "period_start",
      key: "period_start",
    },
    {
      title: "期間迄",
      dataIndex: "period_end",
      key: "period_end",
    },
    {
      title: "需正本",
      dataIndex: "need_original",
      key: "need_original",
      render: (value) => (value === "Y" ? "是" : "否"),
    },
    {
      title: "備註",
      dataIndex: "remark",
      key: "remark",
    },
  ];

  const logColumns = [
    {
      title: "簽核時間",
      dataIndex: "audit_date",
      key: "audit_date",
      render: (value) => moment(value).format("YYYY/MM/DD HH:mm"),
    },
    {
      title: "角色",
      dataIndex: "auditor_type",
      key: "auditor_type",
      render: (value) => (value === "Michael" ? "總經理" : value),
    },
    {
      title: "簽核人",
      dataIndex: "auditor_name",
      key: "auditor_name",
    },
    {
      title: "結果",
      dataIndex: "audit_status",
      key: "audit_status",
      render: (value) => FinDocStatusObj[value] || value,
    },
    {
      title: "意見",
      dataIndex: "audit_command",
      key: "audit_command",
    },
  ];

  const submitApply = async () => {
    if (isUploading) {
      notification.warning({ message: "請稍候", description: "附件上傳中，請待上傳完成後再送出申請。" });
      return;
    }
    const values = await form.validateFields();
    const docItems = normalizeDocItems(values.doc_items);
    const docValidationMessage = validateDocItems(docItems);
    if (docValidationMessage) {
      notification.error({ message: "錯誤", description: docValidationMessage });
      return;
    }
    setSubmitting(true);
    try {
      const nextAuditor = await getNextAuditor(actor.email);
      if (!nextAuditor?.mail) {
        notification.error({ message: "錯誤", description: "無法取得申請人的部門簽核主管。" });
        return;
      }

      const resp = await addFinDoc({
        applicant_name: actor.name,
        applicant_mail: actor.email,
        is_agent: values.is_agent,
        agent_name: values.is_agent === "Y" ? (allUserList.find((user) => user.value === values.agent_name)?.label || values.agent_name) : "",
        agent_mail: values.is_agent === "Y" ? values.agent_mail : "",
        company: values.company,
        apply_reason: values.apply_reason,
        next_auditor_name: normalizeAuditorDisplayName(nextAuditor.displayName, nextAuditor.mail),
        next_auditor_mail: nextAuditor.mail,
        doc_items: docItems,
        file: sourceFiles.map((item) => item.url),
      });

      if (resp?.isSuccess !== false) {
        notification.success({ message: "成功", description: "財務文件申請已送出" });
        navigate("/FinDoc/Application");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitCancel = async () => {
    if (!detail) {
      return;
    }

    Modal.confirm({
      title: `確認取消 FD-${detail.findoc_no}？`,
      onOk: async () => {
        const resp = await cancelFinDoc(detail.findoc_no);
        if (resp?.isSuccess !== false) {
          notification.success({ message: "成功", description: "申請已取消" });
          navigate("/FinDoc/Application");
        }
      },
    });
  };

  const submitAudit = async (approved) => {
    const values = await form.validateFields();
    if (!detail) {
      return;
    }

    if (!approved && !values.audit_command) {
      notification.error({ message: "錯誤", description: "駁回時請填寫意見" });
      return;
    }

    setSubmitting(true);
    try {
      let resp;
      if (approved) {
        if (detail.findoc_status === "pendingMgr" || isCurrentMichaelAuditor(detail, actor)) {
          resp = await mgrAgreeFinDoc({
            findoc_no: detail.findoc_no,
            auditor_name: actor.name,
            auditor_mail: actor.email,
            audit_command: values.audit_command,
            auditor_type: "Michael",
            ...getFinanceAuditorPayload(),
          });
        } else {
          const managerResp = await getNextAuditor(actor.email);
          if (!managerResp?.mail) {
            notification.error({ message: "錯誤", description: "無法取得下一關簽核主管。" });
            return;
          }

          resp = await agreeFinDoc({
            findoc_no: detail.findoc_no,
            auditor_name: actor.name,
            auditor_mail: actor.email,
            audit_command: values.audit_command,
            auditor_type: "上級主管",
            next_auditor_name: normalizeAuditorDisplayName(managerResp.displayName, managerResp.mail),
            next_auditor_mail: managerResp.mail,
          });
        }
      } else if (detail.findoc_status === "pendingMgr") {
        resp = await mgrRejectFinDoc({
          findoc_no: detail.findoc_no,
          auditor_name: actor.name,
          auditor_mail: actor.email,
          audit_command: values.audit_command,
          auditor_type: "Michael",
        });
      } else {
        resp = await rejectFinDoc({
          findoc_no: detail.findoc_no,
          auditor_name: actor.name,
          auditor_mail: actor.email,
          audit_command: values.audit_command,
          auditor_type: "上級主管",
        });
      }

      if (resp?.isSuccess !== false) {
        notification.success({ message: "成功", description: approved ? "簽核已送出" : "駁回已送出" });
        navigate("/FinDoc/PendingAudit");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitFinance = async () => {
    if (isUploading) {
      notification.warning({ message: "請稍候", description: "檔案上傳中，請待上傳完成後再歸檔。" });
      return;
    }
    const values = await form.validateFields();
    if (!resultFiles.length) {
      notification.error({ message: "錯誤", description: "請先上傳財務文件" });
      return;
    }

    setSubmitting(true);
    try {
      const resp = await financeUploadFinDoc({
        findoc_no: detail.findoc_no,
        auditor_name: actor.name,
        auditor_mail: actor.email,
        audit_command: values.finance_comment,
        upload_file_url: resultFiles.map((item) => item.url),
      });
      if (resp?.isSuccess !== false) {
        notification.success({ message: "成功", description: "文件已歸檔完成" });
        navigate("/FinDoc/PendingFinance");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderActionButtons = (isMobileLayout = false) => {
    const ButtonComponent = isMobileLayout ? MobileBtns : PageBtns;
    const buttons = [];

    if (isAddMode) {
      buttons.push({ key: "apply", type: "primary", loading: submitting, disabled: isUploading, onClick: submitApply, label: "送出申請" });
    }

    if (canCancel && !isAuditMode && !isFinanceMode) {
      buttons.push({ key: "cancel", danger: true, onClick: submitCancel, label: "取消申請" });
    }

    if (isAuditMode) {
      buttons.push({ key: "reject", danger: true, loading: submitting, onClick: () => submitAudit(false), label: "駁回" });
      buttons.push({ key: "agree", type: "primary", loading: submitting, onClick: () => submitAudit(true), label: "同意" });
    }

    if (isFinanceMode) {
      buttons.push({ key: "finance", type: "primary", loading: submitting, disabled: isUploading, onClick: submitFinance, label: "上傳並歸檔" });
    }

    buttons.push({ key: "back", onClick: () => navigate(-1), label: "返回" });

    const buttonNodes = buttons.map(({ key, label, ...buttonProps }) => (
      <ButtonComponent key={key} {...buttonProps}>
        {label}
      </ButtonComponent>
    ));

    if (isMobileLayout) {
      return <Row style={{ width: "100%" }}>{buttonNodes}</Row>;
    }

    return (
      <Row justify="end" style={{ width: "100%", color: "black", paddingBottom: "15px" }}>
        <Col flex="none">
          <Space>{buttonNodes}</Space>
        </Col>
      </Row>
    );
  };

  return (
    <PageLayout spinning={pageLoading || submitting}>
      <Col span={24}>
        <Breadcrumb separator=">">
          <Breadcrumb.Item href="/">WiAdvance EIP</Breadcrumb.Item>
          <Breadcrumb.Item href="/FinDoc/Application">財務文件申請</Breadcrumb.Item>
          <Breadcrumb.Item>
            {isAddMode ? "新增申請" : isAuditMode ? "簽核審核" : isFinanceMode ? "財務歸檔" : "申請明細"}
          </Breadcrumb.Item>
        </Breadcrumb>
      </Col>
      <PageTitle>{isAddMode ? "新增申請" : isAuditMode ? "簽核審核" : isFinanceMode ? "財務歸檔" : "申請明細"}</PageTitle>
      {sitConfig.enabled ? (
        <Col span={24} style={{ marginBottom: 16 }}>
          <Alert
            type="warning"
            showIcon
            message={`FinDoc SIT 模式：${actor.name} (${actor.email})`}
            description="此頁動作會套用前端模擬狀態機，可切換主管、Michael、財務同仁身份驗證流程。"
          />
        </Col>
      ) : null}
      <Col span={24} style={{ backgroundColor: "white", padding: isMobile ? "10px" : "10px 15px 15px" }}>
        <Form form={form} {...formLayoutProps}>
          <Divider orientation="left">申請資訊</Divider>
          {/* 排版參考用印：左側 InputPrefix 標籤 + bordered={false} 欄位 */}
          <Row gutter={[24, 8]} style={{ color: "black", paddingBottom: 8 }} align="middle">
            <Col xs={24} md={4}><InputPrefix require={"true"} width={"100%"} label={"申請人"} /></Col>
            <Col xs={24} md={8}>
              <Form.Item name="applicant_name" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }} rules={[{ required: true, message: "請輸入申請人" }]}>
                <Input bordered={false} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}><InputPrefix width={"100%"} label={"申請人信箱"} /></Col>
            <Col xs={24} md={8}>
              <Form.Item name="applicant_mail" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }}>
                <Input bordered={false} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 8]} style={{ color: "black", paddingBottom: 8 }} align="middle">
            <Col xs={24} md={4}><InputPrefix require={"true"} width={"100%"} label={"公司別"} /></Col>
            <Col xs={24} md={8}>
              <Form.Item name="company" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }} rules={[{ required: true, message: "請選擇公司別" }]}>
                <Select bordered={false} disabled={!isAddMode} options={FinDocCompanyOptions} style={{ borderBottom: "1px solid #ddd" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}><InputPrefix width={"100%"} label={"是否代申請"} /></Col>
            <Col xs={24} md={8}>
              <Form.Item name="is_agent" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }}>
                <Radio.Group disabled={!isAddMode}>
                  <Radio value="N">否</Radio>
                  <Radio value="Y">是</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => getFieldValue("is_agent") === "Y" ? (
              <Row gutter={[24, 8]} style={{ color: "black", paddingBottom: 8 }} align="middle">
                <Col xs={24} md={4}><InputPrefix width={"100%"} label={"被代理人"} /></Col>
                <Col xs={24} md={8}>
                  <Form.Item name="agent_name" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }}>
                    <Select
                      showSearch
                      allowClear
                      bordered={false}
                      style={{ borderBottom: "1px solid #ddd" }}
                      disabled={!isAddMode}
                      options={allUserList}
                      onChange={(_, option) => {
                        form.setFieldsValue({ agent_mail: option?.value || "" });
                      }}
                      filterOption={(input, option) => {
                        if (input.search(/^[A-Za-z0-9]*$/) === 0) {
                          return (option?.label ?? "").toLowerCase().startsWith(input.toLowerCase());
                        }
                        return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}><InputPrefix width={"100%"} label={"代理人信箱"} /></Col>
                <Col xs={24} md={8}>
                  <Form.Item name="agent_mail" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }}>
                    <Input bordered={false} disabled />
                  </Form.Item>
                </Col>
              </Row>
            ) : null}
          </Form.Item>
          <Row gutter={[24, 8]} style={{ color: "black", paddingBottom: 8 }}>
            <Col xs={24} md={4}><InputPrefix require={"true"} width={"100%"} label={"申請原因"} /></Col>
            <Col xs={24} md={20}>
              <Form.Item name="apply_reason" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }} rules={[{ required: true, message: "請輸入申請原因" }]}>
                <TextArea rows={4} disabled={!isAddMode} />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">申請明細</Divider>
          {!isAddMode ? (
            <Table rowKey={(record, index) => `${record.doc_type}-${index}`} pagination={false} columns={docColumns} dataSource={detail?.doc_items || []} scroll={{ x: 900 }} />
          ) : (
            <Form.List name="doc_items">
              {(fields, { remove }) => (
                <>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue, setFieldsValue }) => {
                      const docItems = getFieldValue("doc_items") || [];
                      const selectedDocTypes = docItems.map((item) => item.doc_type).filter(Boolean);

                      return (
                        <Form.Item label="文件勾選" required>
                          <Checkbox.Group
                            options={FinDocDocTypeOptions}
                            value={selectedDocTypes}
                            onChange={(checkedValues) => {
                              const nextItems = checkedValues.map((docType) => {
                                const existing = docItems.find((item) => item.doc_type === docType);
                                if (existing) {
                                  return existing;
                                }

                                return {
                                  doc_type: docType,
                                  need_original: docRequirementMap[docType]?.originalRequired ? undefined : "N",
                                };
                              });

                              setFieldsValue({ doc_items: nextItems });
                            }}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                  {fields.map((field) => (
                    <Row gutter={[16, 8]} key={field.key} align="middle">
                      <Col xs={24} md={6}>
                        <Form.Item {...field} label="文件類型" name={[field.name, "doc_type"]} rules={[{ required: true, message: "請選擇文件類型" }]}>
                          <Select options={FinDocDocTypeOptions} disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4}>
                        <Form.Item
                          {...field}
                          label="期間起"
                          name={[field.name, "period_start"]}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const docType = getFieldValue(["doc_items", field.name, "doc_type"]);
                                const requirement = docRequirementMap[docType] || {};
                                if (requirement.periodRequired && !value) {
                                  return Promise.reject(new Error(`請填寫${requirement.periodLabel}`));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <DatePicker picker="month" format="YYYY-MM" placeholder="選擇月份" style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4}>
                        <Form.Item
                          {...field}
                          label="期間迄"
                          name={[field.name, "period_end"]}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const docType = getFieldValue(["doc_items", field.name, "doc_type"]);
                                const requirement = docRequirementMap[docType] || {};
                                if (requirement.periodEndRequired && !value) {
                                  return Promise.reject(new Error("請填寫期間迄"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <DatePicker picker="month" format="YYYY-MM" placeholder="選擇月份" style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4}>
                        <Form.Item
                          {...field}
                          label="需正本"
                          name={[field.name, "need_original"]}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const docType = getFieldValue(["doc_items", field.name, "doc_type"]);
                                const requirement = docRequirementMap[docType] || {};
                                if (requirement.originalRequired && !value) {
                                  return Promise.reject(new Error("請選擇是否需正本"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Radio.Group>
                            <Radio value="N">否</Radio>
                            <Radio value="Y">是</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={5}>
                        <Form.Item
                          {...field}
                          label="備註"
                          name={[field.name, "remark"]}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const docType = getFieldValue(["doc_items", field.name, "doc_type"]);
                                const requirement = docRequirementMap[docType] || {};
                                if (requirement.remarkRequired && !value) {
                                  return Promise.reject(new Error("請填寫備註"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={1} style={{ marginTop: 6 }}>
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      </Col>
                    </Row>
                  ))}
                  {!fields.length ? <div style={{ color: "#7C7C7C", paddingBottom: 12 }}>請先勾選申請文件</div> : null}
                </>
              )}
            </Form.List>
          )}
          <Divider orientation="left">附件</Divider>
          <Space direction="vertical" style={{ width: "100%" }}>
            {isAddMode || isFinanceMode ? (
              <>
                <Upload customRequest={(option) => fileUpload(option, isFinanceMode ? "result" : "source")} onRemove={removeUploadedItem} showUploadList={false} accept="image/jpeg, image/png, image/gif, application/pdf" {...uploadFileProps}>
                  <Button icon={<UploadOutlined />} loading={isUploading}>{isFinanceMode ? "上傳完成文件" : "上傳申請附件"}</Button>
                </Upload>
                <p style={{ color: "red", paddingTop: "5px", marginBottom: 0 }}>僅能上傳 jpg/jpeg/png/gif/pdf 檔</p>
                {isUploading ? <p style={{ color: "#faad14", marginBottom: 0 }}>附件上傳中，請待完成後再送出…</p> : null}
              </>
            ) : null}
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{isFinanceMode ? "歸檔文件" : "原始附件"}</div>
              {renderFileList(isFinanceMode ? resultFiles : sourceFiles)}
            </div>
            {!isAddMode && !isFinanceMode ? (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>已歸檔文件</div>
                {renderFileList((detail?.upload_file_url || []).map((url, index) => ({ uid: `archived-${index}`, name: fileNameFromUrl(url), url })))}
              </div>
            ) : null}
          </Space>
          <Divider orientation="left">簽核流程</Divider>
          <Row gutter={[16, 16]} style={{ color: "black", paddingBottom: 8 }}>
            {signoffProcessList.map((item, index) => (
              <Col
                key={`${item.type}-${item.name}-${index}`}
                xs={12}
                md={4}
                style={{
                  color: detail && getDisplayAuditorName(detail) === item.name ? "blue" : undefined,
                  wordBreak: "break-word",
                }}
              >
                <p style={{ marginBottom: 4 }}>{item.type}</p>
                {item.name}
              </Col>
            ))}
          </Row>
          {!isAddMode && detail ? (
            <>
              <Divider orientation="left">流程資訊</Divider>
              <Row gutter={[16, 8]}>
                <Col xs={24} md={8}>目前狀態：{FinDocStatusObj[detail.findoc_status] || detail.findoc_status}</Col>
                <Col xs={24} md={8}>目前關卡：{getDisplayAuditorName(detail)}</Col>
                <Col xs={24} md={8}>申請時間：{moment(detail.cr_date).format("YYYY/MM/DD HH:mm")}</Col>
              </Row>
            </>
          ) : null}
          {isAuditMode ? (
            <>
              <Divider orientation="left">簽核意見</Divider>
              <Form.Item label="簽核意見" name="audit_command">
                <TextArea rows={4} />
              </Form.Item>
            </>
          ) : null}
          {isFinanceMode ? (
            <>
              <Divider orientation="left">歸檔備註</Divider>
              <Form.Item label="備註" name="finance_comment">
                <TextArea rows={4} />
              </Form.Item>
            </>
          ) : null}
          {!isAddMode ? (
            <>
              <Divider orientation="left">簽核歷程</Divider>
              <Table rowKey={(record, index) => `${record.audit_date}-${index}`} pagination={false} columns={logColumns} dataSource={logs} scroll={{ x: 900 }} />
            </>
          ) : null}
        </Form>
      </Col>
      <Col span={24} style={{ backgroundColor: "white", padding: isMobile ? "0 10px 15px" : "0 15px 15px" }}>
        <BrowserView>{renderActionButtons(false)}</BrowserView>
        <MobileView style={{ marginBottom: "115px" }}>{renderActionButtons(true)}</MobileView>
      </Col>
      <ViewFileModal />
    </PageLayout>
  );
}
